/**
 * Admin Logs & Audit
 * System logs, audit trail, and activity monitoring
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminApiGet } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowsClockwise,
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  Database,
  DownloadSimple,
  FileText,
  Funnel,
  Info,
  MagnifyingGlass,
  Pulse,
  Shield,
  User,
  Warning,
  XCircle
} from '@phosphor-icons/react';
const LogsAudit = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('24h');
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, [typeFilter, levelFilter, dateFilter, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        date_range: dateFilter,
        page: page.toString(),
        page_size: '50'
      });
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const data = await adminApiGet(`/api/admin/logs?${params.toString()}`);
      setLogs(data.logs || []);
      setTotalLogs(data.total || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      // If API fails, show empty state instead of mock data
      setLogs([]);
      setTotalLogs(0);
      toast.error('Failed to load logs - check if audit logging is configured');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         log.user.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || log.type === typeFilter;
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
    return matchesSearch && matchesType && matchesLevel;
  });

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error': return <XCircle size={16} className="text-red-400" />;
      case 'warning': return <Warning size={16} className="text-yellow-400" />;
      case 'success': return <CheckCircle size={16} className="text-green-400" />;
      default: return <Info size={16} className="text-blue-400" />;
    }
  };

  const getLevelBadge = (level) => {
    const colors = {
      error: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      success: 'bg-green-500/20 text-green-400 border-green-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return colors[level] || colors.info;
  };

  const handleExport = () => {
    toast.info('Export feature coming soon');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Logs & Audit Trail</h1>
          <p className="text-gray-400">System logs, user actions, and security events</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2"
          >
            <ArrowsClockwise size={18} />
            ArrowsClockwise
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors flex items-center gap-2"
          >
            <DownloadSimple size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <FileText size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Logs</p>
              <p className="text-2xl font-bold text-white">{totalLogs}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle size={20} className="text-red-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Errors</p>
              <p className="text-2xl font-bold text-white">
                {logs.filter(l => l.level === 'error').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Warning size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Warnings</p>
              <p className="text-2xl font-bold text-white">
                {logs.filter(l => l.level === 'warning').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Shield size={20} className="text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Admin Actions</p>
              <p className="text-2xl font-bold text-white">
                {logs.filter(l => l.type === 'admin').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Types</option>
            <option value="auth">Authentication</option>
            <option value="api">API</option>
            <option value="admin">Admin Actions</option>
            <option value="system">System</option>
            <option value="database">Database</option>
          </select>

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Levels</option>
            <option value="error">Errors</option>
            <option value="warning">Warnings</option>
            <option value="success">Success</option>
            <option value="info">Info</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        {filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={48} className="text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No logs found</h3>
            <p className="text-gray-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredLogs.slice(0, 50).map((log) => (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-white/5"
                  >
                    <td className="px-6 py-4 text-sm text-white whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-400" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getLevelIcon(log.level)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getLevelBadge(log.level)}`}>
                          {log.level}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-white/5 rounded">
                        {log.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-white">
                      {log.action}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {log.user}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400 font-mono">
                      {log.ip}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
        <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-blue-400 font-semibold mb-1">Mock Data</h3>
          <p className="text-sm text-blue-200/80">
            This page is displaying mock log data. To enable real-time logging, implement the backend
            logs endpoint and configure log aggregation (e.g., with ELK stack or CloudWatch).
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogsAudit;
